import React, { useMemo } from 'react';
import { diff_match_patch } from 'diff-match-patch';
import { makeStyles } from 'design-system/product/theme';

interface TextCollectionDiffProps {
  title?: string;
  originalText?: string;
  originalTextColor?: string;
  addedDiffColor?: string;
  newText?: string;
  fullWidth?: boolean;
  className?: string;
  alwaysShowOriginalText?: boolean;
}

const TextCollectionDiff: React.FC<TextCollectionDiffProps> = props => {
  const {
    title,
    fullWidth,
    originalText,
    originalTextColor,
    addedDiffColor,
    newText,
    className,
    alwaysShowOriginalText,
  } = props;
  const classes = useStyles({ fullWidth });
  const diffTexts = useMemo(() => {
    if (originalText && (newText || alwaysShowOriginalText)) {
      // eslint-disable-next-line new-cap
      const dmp = new diff_match_patch();
      const changes = dmp.diff_main(originalText, newText || '');
      dmp.diff_cleanupSemantic(changes);

      // inspired by react-diff
      const diffTexts = changes.map((part, partI) => {
        const backgroundColor = part[0] === 1 ? addedDiffColor || '#bfb' : undefined;
        const strikethrough = part[0] === -1 ? 'line-through' : undefined;
        const textColor = part[0] === 1 || part[0] === -1 ? 'black' : originalTextColor || '#888';
        return (
          <span
            key={`diff_${partI}`}
            style={{
              backgroundColor,
              color: textColor,
              textDecoration: strikethrough,
              textDecorationColor: 'maroon',
            }}
          >
            {part[1]}
          </span>
        );
      });
      return diffTexts;
    }
  }, [originalText, newText]);

  return (
    <div className={className || ''}>
      <div className={classes.container}>
        {title && <span className={classes.title}>{title} (diff)</span>}
        <div style={{ whiteSpace: 'pre-wrap' }}>{diffTexts}</div>
      </div>
    </div>
  );
};

interface StyleProps {
  fullWidth?: boolean;
}

const useStyles = makeStyles(theme => ({
  container: ({ fullWidth }: StyleProps) => ({
    border: '1px solid',
    borderColor: theme.colors.gray20,
    borderRadius: theme.spacing(1),
    padding: '16px',
    margin: fullWidth ? '16px 0px 16px 0px' : '16px',
    width: fullWidth ? '100%' : '90%',
  }),
  title: {
    color: '#000000',
    fontWeight: 500,
    fontSize: '14px',
    lineHeight: '20px',
    marginTop: '4px',
    marginBottom: '12px',
    display: 'inline-block',
  },
}));

export default TextCollectionDiff;
